import isMobilePhone from 'validator/lib/isMobilePhone';

import { DATA_START_ROW } from '@app/features/bulkRequest/constant';
import { validateColumnValue } from '@app/libs/excel/bulkRequest/validateColumnValue';
import Column from '@app/store/bulkRequest/form/Column';
import ColumnType from '@app/store/bulkRequest/form/ColumnType';
import FormValue from '@app/store/transport-layer/bulk-request/bulk-requests/request-tasks/FormValue';

export interface ExcelValidationError {
  row: number;
  column: number;
  message: string;
  type: ColumnType;
}

/**
 * 엑셀 데이터의 유효성을 검증하고 양식에 맞지 않는 데이터가 있는 경우
 * 어디서 어떤 유효성 에러가 발생했는지 에러 정보를 반환합니다.
 */
export function validateFormValues({
  form,
  formValues,
  othersContactInfosInTemplate,
  isSigningInOrder,
}: {
  form: Column[];
  formValues: FormValue[];
  othersContactInfosInTemplate: string[];
  isSigningInOrder: boolean;
}): ExcelValidationError[] {
  const participantContactIndex = form.findIndex((column) => column.type === 'PARTICIPANT_CONTACT');

  const participantAuthNameFormIndex = form.findIndex(
    (column) => column.type === 'VERIFICATION_MOBILE_IDENTIFICATION_NAME'
  );

  const participantAuthPhoneFormIndex = form.findIndex(
    (column) => column.type === 'VERIFICATION_MOBILE_IDENTIFICATION_PHONE'
  );

  const passwordValueFormIndex = form.findIndex(
    (column) => column.type === 'VERIFICATION_PASSWORD'
  );

  const passwordHintFormIndex = form.findIndex(
    (column) => column.type === 'VERIFICATION_PASSWORD_HINT'
  );

  const verificationDCertAuthNameFormIndex = form.findIndex(
    (column) => column.type === 'VERIFICATION_D_CERT_NAME'
  );

  const verificationDCertAuthBizNumFormIndex = form.findIndex(
    (column) => column.type === 'VERIFICATION_D_CERT_BIZ_NUMBER'
  );

  const carbonCopyContactFormIndex = form.findIndex(
    (column) => column.type === 'CARBON_COPY_CONTACT'
  );

  const carbonCopyLocaleFormIndex = form.findIndex(
    (column) => column.type === 'CARBON_COPY_LOCALE'
  );

  return formValues.flatMap((rowData, rowIndex): ExcelValidationError[] => {
    const rowNumber = rowIndex + DATA_START_ROW;
    const rowErrors = rowData.map((columnData, columnIndex): ExcelValidationError | null => {
      const { type } = form[columnIndex];
      return validateColumnValue({
        columnIndex,
        rowIndex: rowNumber,
        type,
        value: columnData,
        othersContactInfosInTemplate,
        isSigningInOrder,
      });
    });

    /**
     * 각 셀 데이터에서 검사할 수 없는 유효성 검사는 row 읽는 loop 주기에 검사합니다.
     * 1. 휴대폰 본인인증 이름이 있는데 휴대폰 번호가 공란인 경우
     * 2. 휴대폰 본인인증 전화번호가 있는데 휴대폰 본인인증 이름이 공란인 경우
     * 3. 접근 암호가 빈칸인데 접근암호 힌트에 값이 있는 경우
     */
    if (rowData[participantAuthNameFormIndex] && !rowData[participantAuthPhoneFormIndex]) {
      rowErrors.push({
        row: rowNumber,
        column: participantAuthPhoneFormIndex,
        message: '휴대폰 본인인증을 설정하려면 이름과 전화번호를 함께 입력해 주세요.',
        type: 'VERIFICATION_MOBILE_IDENTIFICATION_PHONE',
      });
    }
    if (!rowData[participantAuthNameFormIndex] && rowData[participantAuthPhoneFormIndex]) {
      rowErrors.push({
        row: rowNumber,
        column: participantAuthNameFormIndex,
        message: '휴대폰 본인인증을 설정하려면 이름과 전화번호를 함께 입력해 주세요.',
        type: 'VERIFICATION_MOBILE_IDENTIFICATION_NAME',
      });
    }

    if (
      (rowData[participantAuthNameFormIndex] || rowData[participantAuthPhoneFormIndex]) &&
      (rowData[verificationDCertAuthNameFormIndex] || rowData[verificationDCertAuthBizNumFormIndex])
    ) {
      rowErrors.push({
        row: rowNumber,
        column: rowData[verificationDCertAuthNameFormIndex]
          ? verificationDCertAuthNameFormIndex
          : verificationDCertAuthBizNumFormIndex,
        message: '휴대폰 본인인증과 법인공동인증서 중 한가지만 선택할 수 있습니다.',
        type: rowData[verificationDCertAuthNameFormIndex]
          ? 'VERIFICATION_D_CERT_NAME'
          : 'VERIFICATION_D_CERT_BIZ_NUMBER',
      });
    }

    if (
      rowData[participantContactIndex] &&
      isMobilePhone(rowData[participantContactIndex], 'ko-KR') &&
      (rowData[verificationDCertAuthNameFormIndex] || rowData[verificationDCertAuthBizNumFormIndex])
    ) {
      rowErrors.push({
        row: rowNumber,
        column: rowData[verificationDCertAuthNameFormIndex]
          ? verificationDCertAuthNameFormIndex
          : verificationDCertAuthBizNumFormIndex,
        message: '카카오톡, 휴대전화번호 서명자에게는 법인 공동인증서 인증을 설정할 수 없습니다.',
        type: rowData[verificationDCertAuthNameFormIndex]
          ? 'VERIFICATION_D_CERT_NAME'
          : 'VERIFICATION_D_CERT_BIZ_NUMBER',
      });
    }

    if (
      rowData[verificationDCertAuthNameFormIndex] &&
      !rowData[verificationDCertAuthBizNumFormIndex]
    ) {
      rowErrors.push({
        row: rowNumber,
        column: verificationDCertAuthBizNumFormIndex,
        message: '법인공동인증서 인증을 설정하려면 법인명과 사업자등록번호를 함께 입력해 주세요.',
        type: 'VERIFICATION_D_CERT_BIZ_NUMBER',
      });
    }

    if (
      !rowData[verificationDCertAuthNameFormIndex] &&
      rowData[verificationDCertAuthBizNumFormIndex]
    ) {
      rowErrors.push({
        row: rowNumber,
        column: verificationDCertAuthNameFormIndex,
        message: '법인공동인증서 인증을 설정하려면 법인명과 사업자등록번호를 함께 입력해 주세요.',
        type: 'VERIFICATION_D_CERT_NAME',
      });
    }

    if (!rowData[passwordValueFormIndex] && rowData[passwordHintFormIndex]) {
      rowErrors.push({
        row: rowNumber,
        column: passwordHintFormIndex,
        message:
          '접근 암호가 설정되지 않은 양식에 접근 암호 힌트를 입력하셨습니다. 접근 암호 힌트 삭제 후 재시도해주세요.',
        type: 'VERIFICATION_PASSWORD_HINT',
      });
    }

    if (
      (!rowData[carbonCopyContactFormIndex] && rowData[carbonCopyLocaleFormIndex]) ||
      (rowData[carbonCopyLocaleFormIndex] &&
        rowData[carbonCopyContactFormIndex].split(',').length <
          rowData[carbonCopyLocaleFormIndex].split(',').length)
    ) {
      rowErrors.push({
        row: rowNumber,
        column: carbonCopyLocaleFormIndex,
        message:
          '참조자보다 참조자 언어의 개수가 더 많이 설정되었습니다. 개수를 동일하게 설정해 주세요.',
        type: 'CARBON_COPY_LOCALE',
      });
    }

    return rowErrors.filter((item): item is ExcelValidationError => item !== null);
  });
}
